<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { Field } from 'vee-validate'

import CardTitle from '@/components/CardTitle.vue'
import InputCaracterCounter from '@/components/InputCaracterCounter.vue'
import SpecialTitle from '@/components/SpecialTitle.vue'
import MerchantTextFormal from '@/views/loyaltySupports/components/MerchantTextFormal.vue'

import type { LoyaltyMerchantFormTexts } from '@/types/global'

const { t } = useI18n()

const props = defineProps<{
  merchantText: LoyaltyMerchantFormTexts
}>()
const merchantTexts = useVModel(props, 'merchantText')
</script>

<template>
  <TabSubPanel>
    <template #content>
      <CardTitle class="mb-6" :title="t('pages.loyaltyPage.tabs.myTexts.formal.title')" />
      <MerchantTextFormal />
      <SpecialTitle>
        <template #icon>
          <BaseIcon name="HeroDeviceTabletIconOutline" class="h-5 w-5" />
        </template>
        {{ t('pages.loyaltyPage.tabs.myTexts.tablet.title') }}
      </SpecialTitle>
      <BaseDisclosure :title="t('pages.loyaltyPage.tabs.myTexts.tablet.homePage')">
        <div class="grid grid-cols-6 gap-6 px-2 py-8 md:px-8">
          <div v-for="(field, index) in merchantTexts.homeTextsFields" :key="index" class="col-span-6 sm:col-span-2">
            <label :for="index" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
            <Field
              v-slot="{ value, handleChange, handleBlur }"
              v-model="field.value"
              :name="index"
              :rules="field.rules"
            >
              <Textarea
                :id="index"
                class="mt-1"
                :model-value="value"
                @update:model-value="handleChange"
                @blur="handleBlur"
              />
            </Field>
            <InputCaracterCounter
              v-if="field.maxLength"
              :content="field.value"
              :max-length="field.maxLength"
              @truncate-content="field.value = $event"
            />
          </div>
        </div>
      </BaseDisclosure>
      <SpecialTitle>
        <template #icon>
          <BaseIcon name="HeroGiftIconOutline" class="h-5 w-5" />
        </template>
        {{ t('pages.loyaltyPage.tabs.myTexts.loyalty.title') }}
      </SpecialTitle>
      <BaseDisclosure class="mb-4" :title="t('pages.loyaltyPage.tabs.myTexts.loyalty.phone')">
        <div class="grid grid-cols-6 gap-6 px-2 py-8 md:px-8">
          <div v-for="(field, index) in merchantTexts?.loginTextsFields" :key="index" class="col-span-6 sm:col-span-2">
            <label :for="index" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
            <Field
              v-slot="{ value, handleChange, handleBlur }"
              v-model="field.value"
              :name="index"
              :rules="field.rules"
            >
              <Textarea
                :id="index"
                class="mt-1"
                :model-value="value"
                @update:model-value="handleChange"
                @blur="handleBlur"
              />
            </Field>
          </div>
        </div>
      </BaseDisclosure>
      <BaseDisclosure class="mb-4" :title="t('pages.loyaltyPage.tabs.myTexts.loyalty.crm')">
        <div class="grid grid-cols-6 gap-6 px-2 py-8 md:px-8">
          <div
            v-for="(field, index) in merchantTexts?.questionsTextsFields"
            :key="index"
            class="col-span-6 sm:col-span-2"
          >
            <label :for="index" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
            <Field
              v-slot="{ value, handleChange, handleBlur }"
              v-model="field.value"
              :name="index"
              :rules="field.rules"
            >
              <Textarea
                :id="index"
                class="mt-1"
                :model-value="value"
                @update:model-value="handleChange"
                @blur="handleBlur"
              />
            </Field>
          </div>
        </div>
      </BaseDisclosure>
      <BaseDisclosure class="mb-4" :title="t('pages.loyaltyPage.tabs.myTexts.loyalty.pointsWon')">
        <div class="grid grid-cols-6 gap-6 px-2 py-8 md:px-8">
          <div v-for="(field, index) in merchantTexts?.pointsTextsFields" :key="index" class="col-span-6 sm:col-span-2">
            <label :for="index" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
            <Field
              v-slot="{ value, handleChange, handleBlur }"
              v-model="field.value"
              :name="index"
              :rules="field.rules"
            >
              <Textarea
                :id="index"
                class="mt-1"
                :model-value="value"
                @update:model-value="handleChange"
                @blur="handleBlur"
              />
            </Field>
          </div>
        </div>
      </BaseDisclosure>
      <BaseDisclosure :title="t('pages.loyaltyPage.tabs.myTexts.loyalty.pointsWonIntegration')">
        <div class="grid grid-cols-6 gap-6 px-2 py-8 md:px-8">
          <div v-for="(field, index) in merchantTexts?.orderTextsFields" :key="index" class="col-span-6 sm:col-span-2">
            <label :for="index" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
            <Field
              v-slot="{ value, handleChange, handleBlur }"
              v-model="field.value"
              :name="index"
              :rules="field.rules"
            >
              <Textarea
                :id="index"
                class="mt-1"
                :model-value="value"
                @update:model-value="handleChange"
                @blur="handleBlur"
              />
            </Field>
          </div>
        </div>
      </BaseDisclosure>
      <SpecialTitle>
        <template #icon>
          <BaseIcon name="HeroDocumentPlusIconOutline" class="h-5 w-5" />
        </template>
        {{ t('pages.loyaltyPage.tabs.myTexts.rewards.title') }}
      </SpecialTitle>
      <BaseDisclosure class="mb-4" :title="t('pages.loyaltyPage.tabs.myTexts.rewards.rewardsTexts')">
        <div class="grid grid-cols-6 gap-6 px-2 py-8 md:px-8">
          <div
            v-for="(field, index) in merchantTexts?.rewardsTextsFields"
            :key="index"
            class="col-span-6 sm:col-span-2"
          >
            <label :for="index" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
            <Field
              v-slot="{ value, handleChange, handleBlur }"
              v-model="field.value"
              :name="index"
              :rules="field.rules"
            >
              <Textarea
                :id="index"
                class="mt-1"
                :model-value="value"
                @update:model-value="handleChange"
                @blur="handleBlur"
              />
            </Field>
          </div>
        </div>
      </BaseDisclosure>
      <BaseDisclosure class="mb-4" :title="t('pages.loyaltyPage.tabs.myTexts.rewards.rewardWon')">
        <div class="grid grid-cols-6 gap-6 px-2 py-8 md:px-8">
          <div
            v-for="(field, index) in merchantTexts?.rewardWonTextsFields"
            :key="index"
            class="col-span-6 sm:col-span-2"
          >
            <label :for="index" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
            <Field
              v-slot="{ value, handleChange, handleBlur }"
              v-model="field.value"
              :name="index"
              :rules="field.rules"
            >
              <Textarea
                :id="index"
                class="mt-1"
                :model-value="value"
                @update:model-value="handleChange"
                @blur="handleBlur"
              />
            </Field>
          </div>
        </div>
      </BaseDisclosure>
    </template>
  </TabSubPanel>
</template>
